import { useState, useEffect, useRef } from "react";
import {
  Download,
  X,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Globe,
  Server,
  Layers,
  Database,
} from "lucide-react";
import { generateDocker } from "../../API/Company-admin/userApi";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import DatabaseSchema from "./database-schema";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const SAMPLE_BASE_URLS = [
  { url: "https://dev-backend1.redacted.com", description: "Development" },
  { url: "https://staging-backend1.redacted.com", description: "Staging" },
  { url: "https://backend1.redacted.com", description: "Production" },
  { url: "https://dev-backend2.redacted.com", description: "Dev Backend 2" },
  {
    url: "https://staging-backend2.redacted.com",
    description: "Staging Backend 2",
  },
  { url: "http://localhost:3000", description: "Local Development" },
];

const DEFAULT_PLACEHOLDERS = [
  {
    baseUrl: "https://dev-backend1.redacted.com",
    appUrl: "/api/v1",
    baseDescription: "BASE URL 1",
  },
  {
    baseUrl: "https://staging-backend1.redacted.com",
    appUrl: "/api/v1",
    baseDescription: "BASE URL 2",
  },
  {
    baseUrl: "https://backend1.redacted.com",
    appUrl: "/api/v1",
    baseDescription: "BASE URL 3",
  },
  {
    baseUrl: "https://dev-backend2.redacted.com",
    appUrl: "/api/v2",
    baseDescription: "BASE URL 4",
  },
  {
    baseUrl: "https://staging-backend2.redacted.com",
    appUrl: "/api/v2",
    baseDescription: "BASE URL 5",
  },
];

// Sample database URLs
const SAMPLE_DB_URLS = [
  {
    url: "postgresql://user:password@dev-db.redacted.com:5432/mydb",
    description: "Development DB",
  },
  {
    url: "postgresql://user:password@staging-db.redacted.com:5432/mydb",
    description: "Staging DB",
  },
  {
    url: "postgresql://user:password@prod-db.redacted.com:5432/mydb",
    description: "Production DB",
  },
  {
    url: "mysql://root:password@localhost:3306/mydb",
    description: "Local MySQL",
  },
  {
    url: "mongodb://user:password@mongo.redacted.com:27017/mydb",
    description: "MongoDB",
  },
];

export default function DockerConfigModal(props) {
  const { isGenerateDisabled, data, dockerName, selectedRows } = props;
  const { userInfo } = useSelector((state) => state.userLogin);
  const token = userInfo?.token || null;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [previousUrls, setPreviousUrls] = useState([]);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [dockerType, setDockerType] = useState("frontend");
  const [isDbDropdownOpen, setIsDbDropdownOpen] = useState(false);
  const [databaseUrl, setDatabaseUrl] = useState("");
  const [isValidDbUrl, setIsValidDbUrl] = useState(true);
  const [databaseFormat, setDatabaseFormat] = useState("json");
  const [isSchemaCollapsed, setIsSchemaCollapsed] = useState(false);
  const [message, setMessage] = useState("");
  const [dockerUrl, setDockerUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const iframeRef = useRef(null);

  const handleDbUrlChange = (value) => {
    setDatabaseUrl(value);
    setIsValidDbUrl(validateDbUrl(value));
  };

  const toggleDbDropdown = () => {
    setIsDbDropdownOpen(!isDbDropdownOpen);
    setOpenDropdownIndex(null);
  };

  const validateDbUrl = (url) => {
    if (!url.trim()) {
      return true; // Empty is valid for our purposes
    }

    // Check for common database URL patterns
    const dbPatterns = [
      /^postgresql:\/\//i,
      /^mysql:\/\//i,
      /^mongodb:\/\//i,
      /^sqlite:\/\//i,
      /^redis:\/\//i,
    ];

    return dbPatterns.some((pattern) => pattern.test(url));
  };

  const [urlPairs, setUrlPairs] = useState(
    Array(5)
      .fill(null)
      .map(() => ({
        baseUrl: "",
        appUrl: "",
        isValidBase: true,
        isValidApp: true,
      }))
  );

  useEffect(() => {
    const savedUrls = localStorage.getItem("previousBaseUrls");
    if (savedUrls) {
      try {
        const parsed = JSON.parse(savedUrls);
        // Handle both old format (string[]) and new format ({url, description}[])
        if (Array.isArray(parsed) && parsed.length > 0) {
          if (typeof parsed[0] === "string") {
            // Convert old format to new format
            setPreviousUrls(parsed.map((url) => ({ url, description: "" })));
          } else {
            setPreviousUrls(parsed);
          }
        }
      } catch (e) {
        // If parsing fails, use sample URLs
        setPreviousUrls(SAMPLE_BASE_URLS);
      }
    } else {
      // If no previous URLs exist, use sample URLs
      setPreviousUrls(SAMPLE_BASE_URLS);
    }
  }, []);

  const saveUrlToHistory = (url, description = "") => {
    if (!url.trim()) return;

    const updatedUrls = [
      { url, description },
      ...previousUrls.filter((item) => item.url !== url),
    ];
    // Limit to 10 most recent URLs
    const limitedUrls = updatedUrls.slice(0, 10);
    setPreviousUrls(limitedUrls);
    localStorage.setItem("previousBaseUrls", JSON.stringify(limitedUrls));
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setIsSubmitting(false);
    setIsSuccess(false);
  };

  const toggleDropdown = (index) => {
    setOpenDropdownIndex(openDropdownIndex === index ? null : index);
  };

  const selectPreviousUrl = (urlItem, index) => {
    const newUrlPairs = [...urlPairs];
    newUrlPairs[index] = {
      ...newUrlPairs[index],
      baseUrl: urlItem.url,
      isValidBase: true,
    };
    setUrlPairs(newUrlPairs);
    setOpenDropdownIndex(null);
  };

  const validateUrl = (url, isBase) => {
    if (!url.trim()) {
      return true; // Empty is valid for our purposes
    }

    try {
      // Check if it's a valid URL format for base URLs
      if (isBase) {
        new URL(url.startsWith("http") ? url : `http://${url}`);
      }
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleBaseUrlChange = (value, index) => {
    const isValid = validateUrl(value, true);
    const newUrlPairs = [...urlPairs];
    newUrlPairs[index] = {
      ...newUrlPairs[index],
      baseUrl: value,
      isValidBase: isValid,
    };
    setUrlPairs(newUrlPairs);
  };

  const handleAppUrlChange = (value, index) => {
    const isValid = validateUrl(value, false);
    const newUrlPairs = [...urlPairs];
    newUrlPairs[index] = {
      ...newUrlPairs[index],
      appUrl: value,
      isValidApp: isValid,
    };
    setUrlPairs(newUrlPairs);
  };

  // Helper: Convert base64 string to Blob
  function b64toBlob(b64Data, contentType = "", sliceSize = 512) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length)
        .fill()
        .map((_, i) => slice.charCodeAt(i));
      byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, { type: contentType });
  }

  // Main function to call
  const downloadAllAsZip = async (response, docker_image_name) => {
    const zip = new JSZip();
    const folder = zip.folder("BuildPackage");

    // Add collections.json
    if (response.collections) {
      folder.file(
        "collections.json",
        JSON.stringify(response.collections, null, 2)
      );
    }

    // Add archiveBase64 as archive.zip
    if (response.archiveBase64) {
      const archiveBlob = b64toBlob(response.archiveBase64, "application/zip");
      folder.file("archive.zip", archiveBlob);
    }

    // async function downloadTarFile(url, fileName) {
    //   try {
    //     const link = document.createElement("a");
    //     link.href = url;
    //     link.download = fileName;
    //     document.body.appendChild(link);
    //     link.click();
    //     document.body.removeChild(link);
    //   } catch (err) {
    //     console.error(`Error downloading ${fileName}:`, err);
    //   }
    // }

    function triggerDownload(url, filename) {
      return new Promise((resolve) => {
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);

        // Programmatically trigger download
        link.dispatchEvent(new MouseEvent("click"));

        document.body.removeChild(link);

        // Resolve immediately — some browsers may need a tick
        setTimeout(resolve, 100);
      });
    }

    async function downloadBothFiles(response) {
      const frontendURL = response.url.frontend;
      const backendURL = response.url.backend;
      const frontendFile = `${docker_image_name}_frontend.tar`;
      const backendFile = `${docker_image_name}_backend.tar`;

      // Trigger downloads with slight offset to avoid browser block
      await triggerDownload(frontendURL, frontendFile);
      await triggerDownload(backendURL, backendFile);
    }

    // async function downloadTarFile(url, fileName) {
    //   try {
    //     const response = await fetch(url);
    //     if (!response.ok) throw new Error(`HTTP error ${response.status}`);
    //     const blob = await response.blob();
    //     const blobUrl = window.URL.createObjectURL(blob);
    //     console.log(`${fileName} downloaded.`);

    //     const link = document.createElement("a");
    //     link.href = blobUrl;
    //     link.download = fileName;
    //     document.body.appendChild(link);
    //     link.click();
    //     document.body.removeChild(link);

    //     window.URL.revokeObjectURL(blobUrl);
    //     console.log(`${fileName} downloaded.`);
    //   } catch (err) {
    //     console.error(`Error downloading ${fileName}:`, err);
    //   }
    // }

    // Add Docker TAR file from URL
    // if (response.url) {
    //   try {
    //     const dockerRes = await fetch(response.url);
    //     const dockerBlob = await dockerRes.blob();
    //     folder.file(`${docker_image_name}.tar`, dockerBlob);
    //   } catch (err) {
    //     console.error("Error fetching Docker file:", err);
    //   }
    // }

    if (response.url && response.buildResponse.build_type === "both") {
      console.log(response.buildResponse?.build_type === "both");

      // Download frontend
      try {
        await downloadBothFiles(response);
      } catch (err) {
        console.error("Error downloading frontend Docker file:", err);
      }
    } else if (response.url) {
      // For single file (e.g., frontend only or backend only)
      try {
        await triggerDownload(response.url, "frontendFile");
      } catch (err) {
        console.error("Error fetching Docker file:", err);
      }
    }

    // Generate ZIP and trigger download
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, "Collection.zip");
    toast.success(
      "Docker generated successfully! Downloading BuildPackage.zip..."
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate all URLs
    let isValid = true;
    const newUrlPairs = [...urlPairs];

    urlPairs.forEach((pair, index) => {
      if (pair.baseUrl && !validateUrl(pair.baseUrl, true)) {
        newUrlPairs[index].isValidBase = false;
        isValid = false;
      }
      if (pair.appUrl && !validateUrl(pair.appUrl, false)) {
        newUrlPairs[index].isValidApp = false;
        isValid = false;
      }
    });

    setUrlPairs(newUrlPairs);

    if (isValid) {
      setIsSubmitting(false);

      // Save valid base URLs to history
      urlPairs.forEach((pair, index) => {
        if (pair.baseUrl) {
          // Try to find a description from the placeholders
          const description =
            DEFAULT_PLACEHOLDERS[index]?.baseDescription || "";
          saveUrlToHistory(pair.baseUrl, description);
        }
      });

      const config = {};
      urlPairs.forEach((pair, index) => {
        if (pair.appUrl) {
          config[`baseUrl${index + 1}`] = pair.appUrl;
        }
      });

      const payload = {
        projectId: data?.id,
        organisationId: userInfo.organisation_id,
        pageIds: selectedRows,
        typeOfDownload: dockerType,
        imageName: dockerName,
        downloadCollections: {
          format: databaseFormat,
        },
        ...(config && Object.keys(config).length > 0 && { config }),
      };

      let toastId;

      try {
        setIsSubmitting(true);
        toastId = toast.info(
          "Docker download underway. You can continue working, but please avoid logging out until the process completes.",
          {
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: false,
          }
        );
        // let maxRetries = 5;
        // let retryCount = 0;
        // let response = null;

        // const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

        // while (retryCount < maxRetries) {
        //   try {
        //     response = await generateDocker(payload, token);
        //     if (response?.status === 200 || response?.url) break;
        //   } catch (error) {
        //     console.error(`Attempt ${retryCount + 1} failed`, error);
        //   }
        //   retryCount++;
        //   await sleep(2000); // Retry delay of 2 seconds
        // }

        const response = await generateDocker(payload, token);

        if (response?.url) {
          await downloadAllAsZip(response, dockerName);
        }

        setIsSubmitting(false);
        setIsSuccess(true);
        setMessage("Docker image generation complete!");

        toast.dismiss(toastId);
      } catch (error) {
        setIsSubmitting(false);
        toast.error(
          error?.response?.data?.message || "Docker generation failed",
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
      } finally {
        setIsSubmitting(false);
        setIsSuccess(false);
        toast.dismiss(toastId);
      }
    }
  };

  const handleDockerTypeChange = (type) => {
    setDockerType(type);
  };

  const selectPreviousDbUrl = (urlItem) => {
    setDatabaseUrl(urlItem.url);
    setIsValidDbUrl(true);
    setIsDbDropdownOpen(false);
  };

  const triggerDownload = () => {
    const downloadUrl = dockerUrl;
    if (iframeRef.current) {
      iframeRef.current.src = downloadUrl;
    }
  };

  const isShowingSampleDbUrls = () => {
    const savedUrls = localStorage.getItem("previousDbUrls");
    return !savedUrls;
  };

  // Get placeholder for a specific index
  const getPlaceholder = (index, isBase) => {
    if (index < DEFAULT_PLACEHOLDERS.length) {
      return isBase
        ? DEFAULT_PLACEHOLDERS[index].baseUrl
        : DEFAULT_PLACEHOLDERS[index].appUrl;
    }
    return isBase ? "https://example.com" : "/api";
  };

  // Get description for a specific index
  const getDescription = (index) => {
    if (index < DEFAULT_PLACEHOLDERS.length) {
      return DEFAULT_PLACEHOLDERS[index].baseDescription;
    }
    return "";
  };

  useEffect(() => {
    if (data) {
      const updatedUrlPairs = urlPairs.map((pair, index) => ({
        ...pair,
        baseUrl: data[`baseUrl${index + 1}`] || "",
        appUrl: data[`appUrl${index + 1}`] || "", // If you have these
      }));
      setUrlPairs(updatedUrlPairs);
    }
  }, [data]);

  const handleCopy = () => {
    navigator.clipboard.writeText(dockerUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // reset after 2s
    });
  };

  return (
    <div>
      <iframe
        ref={iframeRef}
        style={{ display: "none" }}
        title="download-frame"
      ></iframe>
      {dockerUrl && (
        <button
          // onClick={handleCopy}
          onClick={triggerDownload}
          className={`${
            isGenerateDisabled ? "bg-red-400" : "bg-red-600 hover:bg-red-700"
          } mb-2 flex items-center justify-center gap-2 text-xs text-white font-medium py-2 px-4 rounded transition-colors duration-200`}
        >
          <Download className="w-5 h-5" />
          {/* {copied ? "COPIED!" : "COPY DOCKER LINK"} */}
          Download Docker
        </button>
      )}
      {/* Docker Generate Button */}
      <button
        onClick={openModal}
        disabled={isGenerateDisabled}
        className={`${
          isGenerateDisabled ? "bg-red-400" : "bg-red-600 hover:bg-red-700"
        } flex items-center justify-center gap-2  text-white font-medium py-2 px-4 rounded transition-colors duration-200`}
      >
        <Download className="w-5 h-5" />
        GENERATE DOCKER
      </button>

      {/* Modal Backdrop */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          {/* Modal Content */}
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 flex flex-col max-h-[80vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-red-600 text-white px-6 py-4 flex items-center justify-between shrink-0">
              <h3 className="text-lg font-medium">Docker Configuration</h3>
              <button
                onClick={closeModal}
                className="text-white hover:text-red-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="modal-scrollable overflow-y-auto flex-grow">
              <form onSubmit={handleSubmit} className="p-6">
                <div className="mb-6">
                  <h5 className="font-semibold text-gray-800 mb-3">
                    Docker Type
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Frontend Only Option */}
                    <label
                      className={`
                      cursor-pointer rounded-lg border-2 transition-all duration-200 bg-gray-200
                      ${
                        dockerType === "frontend"
                          ? "border-red-500 bg-red-50"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }
                    `}
                    >
                      <input
                        type="radio"
                        name="dockerType"
                        className="sr-only"
                        checked={dockerType === "frontend"}
                        onChange={() => handleDockerTypeChange("frontend")}
                      />
                      <div className="p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div
                            className={`
                            p-1.5 rounded-full 
                            ${
                              dockerType === "frontend"
                                ? "bg-red-100 text-red-600"
                                : "bg-gray-100 text-gray-500"
                            }
                          `}
                          >
                            <Globe className="w-5 h-5" />
                          </div>
                          {dockerType === "frontend" && (
                            <div className="flex items-center justify-center w-4 h-4 bg-red-500 rounded-full">
                              <CheckCircle className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                        <h5
                          className={`font-medium ${
                            dockerType === "frontend"
                              ? "text-red-700"
                              : "text-gray-700"
                          }`}
                        >
                          Frontend Only
                        </h5>
                        <p className="text-xs text-gray-500 mt-1">
                          Generate Docker for frontend application only
                        </p>
                      </div>
                    </label>

                    {/* Backend Only Option */}
                    <label
                      className={`
                      cursor-pointer rounded-lg border-2 transition-all duration-200 bg-gray-200
                      ${
                        dockerType === "backend"
                          ? "border-red-500 bg-red-50"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }
                    `}
                    >
                      <input
                        type="radio"
                        name="dockerType"
                        className="sr-only"
                        readOnly
                        checked={dockerType === "backend"}
                        onChange={() => handleDockerTypeChange("backend")}
                      />
                      <div className="p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div
                            className={`
                            p-1.5 rounded-full 
                            ${
                              dockerType === "backend"
                                ? "bg-red-100 text-red-600"
                                : "bg-gray-100 text-gray-500"
                            }
                          `}
                          >
                            <Server className="w-5 h-5" />
                          </div>
                          {dockerType === "backend" && (
                            <div className="flex items-center justify-center w-4 h-4 bg-red-500 rounded-full">
                              <CheckCircle className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                        <h5
                          className={`font-medium ${
                            dockerType === "backend"
                              ? "text-red-700"
                              : "text-gray-700"
                          }`}
                        >
                          Backend Only
                        </h5>
                        <p className="text-xs text-gray-500 mt-1">
                          Generate Docker for backend services only
                        </p>
                      </div>
                    </label>

                    {/* Frontend & Backend Option */}
                    <label
                      className={`
                      cursor-pointer rounded-lg border-2 transition-all duration-200 bg-gray-200
                      ${
                        dockerType === "both"
                          ? "border-red-500 bg-red-50"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }
                    `}
                    >
                      <input
                        type="radio"
                        name="dockerType"
                        className="sr-only"
                        readOnly
                        checked={dockerType === "both"}
                        onChange={() => handleDockerTypeChange("both")}
                      />
                      <div className="p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div
                            className={`
                            p-1.5 rounded-full 
                            ${
                              dockerType === "both"
                                ? "bg-red-100 text-red-600"
                                : "bg-gray-100 text-gray-500"
                            }
                          `}
                          >
                            <Layers className="w-5 h-5" />
                          </div>
                          {dockerType === "both" && (
                            <div className="flex items-center justify-center w-4 h-4 bg-red-500 rounded-full">
                              <CheckCircle className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                        <h5
                          className={`font-medium ${
                            dockerType === "both"
                              ? "text-red-700"
                              : "text-gray-700"
                          }`}
                        >
                          Frontend & Backend
                        </h5>
                        <p className="text-xs text-gray-500 mt-1">
                          Generate Docker for complete application stack
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Database URL Configuration */}
                <DatabaseSchema
                  projectId={data?.id}
                  onFormatChange={(format) => setDatabaseFormat(format)}
                />
                {(dockerType === "frontend" || dockerType === "both") && (
                  <div className="mb-6">
                    <p className="text-gray-600 mb-6">
                      Please specify the BASE_URLs and corresponding APP_URLs
                      for your Docker configuration.
                    </p>

                    {/* Header Row */}
                    <div className="grid grid-cols-12 gap-4 mb-2">
                      <div className="col-span-1"></div>
                      <div className="col-span-5">
                        <h5 className="font-semibold text-gray-800">
                          Base URLs
                        </h5>
                      </div>
                      <div className="col-span-6">
                        <h5 className="font-semibold text-gray-800">
                          App URLs
                        </h5>
                      </div>
                    </div>

                    {/* URL Pairs */}
                    {urlPairs.map((pair, index) => (
                      <div key={index} className="grid grid-cols-12 gap-4 mb-2">
                        {/* Number Column */}
                        <div className="col-span-1 flex items-center justify-end">
                          <span className="text-gray-600 font-medium text-right">
                            {index + 1})
                          </span>
                        </div>

                        {/* App URL Column */}
                        <div className="col-span-5">
                          <div className="text-xs text-gray-500 mb-1 h-4">
                            {getDescription(index)}
                          </div>
                          <input
                            type="text"
                            value={pair.baseUrl}
                            readOnly
                            placeholder={getPlaceholder(index, false)}
                            className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:outline-none transition-colors ${
                              pair.isValidApp
                                ? "border-gray-300 focus:border-red-500 focus:ring-red-200"
                                : "border-red-500 focus:border-red-500 focus:ring-red-200"
                            }`}
                          />
                          {!pair.isValidApp && (
                            <p className="mt-1 text-sm text-red-600">
                              Please enter a valid path
                            </p>
                          )}
                        </div>

                        {/* Base URL Column */}
                        <div className="col-span-5 relative">
                          {/* Description label */}
                          <div className="text-xs text-gray-500 mb-1 h-4">
                            {getDescription(index)}
                          </div>
                          <div className="flex">
                            <input
                              type="text"
                              value={pair.appUrl}
                              onChange={(e) =>
                                handleAppUrlChange(e.target.value, index)
                              }
                              placeholder={getPlaceholder(index, true)}
                              className={`w-full px-3 py-2 border rounded-l-md focus:ring-2 focus:outline-none transition-colors ${
                                pair.isValidBase
                                  ? "border-gray-300 focus:border-red-500 focus:ring-red-200"
                                  : "border-red-500 focus:border-red-500 focus:ring-red-200"
                              }`}
                            />
                          </div>

                          {/* Error message */}
                          {!pair.isValidBase && (
                            <p className="mt-1 text-sm text-red-600 absolute">
                              Please enter a valid URL
                            </p>
                          )}
                        </div>
                      </div>
                    ))}

                    <div className="mt-4 text-xs text-gray-500">
                      Click the dropdown to view available BASE_URLs or enter
                      custom URLs
                    </div>
                  </div>
                )}
              </form>
            </div>
            <div className="border-t border-gray-200 p-4 bg-gray-50 shrink-0">
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={`flex items-center justify-center gap-2 px-4 py-2 rounded-md text-white transition-colors ${
                    isSubmitting || isSuccess
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-red-600 hover:bg-red-700"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Generating...
                    </>
                  ) : isSuccess ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Generate Docker
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
